import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bookmarksService, type BookmarkDto } from '../api/bookmarksService';
import type { Bookmark } from '../components/reader/types';
import { useState, useEffect } from 'react';
import { isNetworkOffline } from '../utils/offlineDetection';

/**
 * Convert backend BookmarkDto to frontend Bookmark type
 */
function mapDtoToBookmark(dto: BookmarkDto): Bookmark {
  return {
    id: dto.id,
    bookId: dto.bookId,
    userId: dto.userId,
    location: dto.location,
    locationLabel: dto.locationLabel,
    note: dto.note,
    createdAt: new Date(dto.creationTime),
    updatedAt: new Date(dto.lastModificationTime || dto.creationTime),
  };
}

/**
 * Hook to manage bookmarks for a specific book
 * Supports both online and offline modes
 */
export function useBookmarks(bookId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['bookmarks', bookId];
  const [isOffline, setIsOffline] = useState(isNetworkOffline());

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch bookmarks (with offline fallback)
  const { data: bookmarks = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      // Try online first
      if (!isNetworkOffline()) {
        try {
          const dtos = await bookmarksService.getMyBookmarks(bookId);
          return dtos.map(mapDtoToBookmark);
        } catch (err) {
          console.warn('Failed to fetch bookmarks from API, falling back to offline:', err);
          // Fall through to offline mode
        }
      }

      // Offline mode: load from IndexedDB
      console.log('📴 Loading bookmarks from offline storage...');
      const { offlineContentService } = await import('../services/offline/OfflineContentService');
      const offlineBookmarks = await offlineContentService.getOfflineBookmarks(bookId);

      // Convert BookmarkData to Bookmark
      return offlineBookmarks.map((b) => ({
        id: b.id,
        bookId: b.bookId,
        userId: '', // Not stored offline
        location: b.location,
        locationLabel: b.label || '',
        note: undefined,
        createdAt: b.createdAt,
        updatedAt: b.createdAt,
      }));
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Create bookmark mutation
  const createBookmark = useMutation({
    mutationFn: async ({
      location,
      locationLabel,
      note,
    }: {
      location: string | number;
      locationLabel: string;
      note?: string;
    }) => {
      const dto = await bookmarksService.createMyBookmark({
        bookId,
        location: String(location),
        locationLabel,
        note,
      });
      return mapDtoToBookmark(dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Update bookmark mutation
  const updateBookmark = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) => {
      const dto = await bookmarksService.updateMyBookmark(id, { note });
      return mapDtoToBookmark(dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Delete bookmark mutation
  const deleteBookmark = useMutation({
    mutationFn: async (id: string) => {
      await bookmarksService.deleteMyBookmark(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Check if location is bookmarked
  const isBookmarked = async (location: string | number): Promise<boolean> => {
    return await bookmarksService.isBookmarked(bookId, String(location));
  };

  return {
    bookmarks,
    isLoading,
    error,
    isOffline, // NEW: Expose offline status to UI
    createBookmark: isOffline
      ? async () => {
          throw new Error('Cannot create bookmarks in offline mode');
        }
      : createBookmark.mutateAsync,
    updateBookmark: isOffline
      ? async () => {
          throw new Error('Cannot update bookmarks in offline mode');
        }
      : updateBookmark.mutateAsync,
    deleteBookmark: isOffline
      ? async () => {
          throw new Error('Cannot delete bookmarks in offline mode');
        }
      : deleteBookmark.mutateAsync,
    isBookmarked,
    isCreating: createBookmark.isPending,
    isUpdating: updateBookmark.isPending,
    isDeleting: deleteBookmark.isPending,
  };
}
